/**
 * Awarding an MYP achievement level.
 *
 * The gesture is the real one a teacher makes: read the four descriptors, decide which one
 * the work actually matches, then choose within it. A 0–8 spinner asks them to do that
 * conversion in their head and then records only the answer, which is why moderation cannot
 * reconstruct the decision afterwards.
 *
 * 0 sits outside the four cards on purpose. It is an award — the teacher looked and found
 * nothing the descriptors describe — and it is not the bottom of the "limited" band.
 */

import { useState } from "react";
import { BandChip, S, type Band } from "../../design/kit";

/** The two levels each descriptor band covers. Order matches the rubric's own order. */
const LEVELS: Record<Band, number[]> = {
  no_evidence: [],
  not_yet: [1, 2],
  approaching: [3, 4],
  secure: [5, 6],
  strong: [7, 8],
};

const BAND_ORDER: Band[] = ["not_yet", "approaching", "secure", "strong"];

export function CriterionPanel({
  rubric,
  letter,
  mark,
  onMark,
}: {
  rubric: { level: Band; descriptor: string }[];
  letter?: string;
  mark?: number;
  onMark: (level: number) => void;
}) {
  const bandOf = (m?: number) =>
    m === undefined ? undefined : BAND_ORDER.find((b) => LEVELS[b].includes(m));
  const [openBand, setOpenBand] = useState<Band | undefined>(bandOf(mark));

  const cards = BAND_ORDER.map((band) => ({
    band,
    descriptor: rubric.find((r) => r.level === band)?.descriptor ?? "",
  }));

  return (
    <div style={{ marginTop: "16px" }}>
      <span style={S.sectionLabel}>
        {letter ? `Criterion ${letter} — which descriptor does this work match?` : "Which descriptor does this work match?"}
      </span>

      <div style={{ display: "grid", gap: "8px", marginTop: "8px" }}>
        {cards.map((c) => {
          const on = openBand === c.band;
          return (
            <div key={c.band}>
              <button
                type="button"
                aria-pressed={on}
                onClick={() => setOpenBand(on ? undefined : c.band)}
                style={{
                  width: "100%",
                  minHeight: "44px",
                  textAlign: "left",
                  display: "flex",
                  gap: "11px",
                  alignItems: "flex-start",
                  padding: "11px 13px",
                  borderRadius: "9px",
                  cursor: "pointer",
                  background: on ? "var(--c-surface-2)" : "transparent",
                  border: on ? "1px solid var(--c-brand)" : "1px solid rgba(17,20,28,.12)",
                }}
              >
                {/* Never colour alone — the band carries its label wherever it appears. */}
                <span style={{ width: "112px", flex: "none" }}>
                  <BandChip band={c.band} />
                </span>
                <span style={{ ...S.mutedSm, lineHeight: 1.55 }}>
                  {c.descriptor || "No descriptor yet — confirm it on the unit plan."}
                </span>
              </button>

              {on ? (
                <div style={{ display: "flex", gap: "8px", padding: "8px 0 4px 123px", flexWrap: "wrap" }}>
                  {LEVELS[c.band].map((lvl) => (
                    <button
                      key={lvl}
                      type="button"
                      aria-pressed={mark === lvl}
                      onClick={() => onMark(lvl)}
                      style={{
                        minWidth: "44px",
                        minHeight: "44px",
                        borderRadius: "9px",
                        cursor: "pointer",
                        fontSize: "0.9375rem",
                        fontWeight: 600,
                        background: mark === lvl ? "var(--c-brand)" : "transparent",
                        color: mark === lvl ? "#fff" : "var(--c-ink)",
                        border: "1px solid rgba(17,20,28,.16)",
                      }}
                    >
                      {lvl}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      <button
        type="button"
        aria-pressed={mark === 0}
        onClick={() => onMark(0)}
        style={{
          marginTop: "10px",
          minHeight: "44px",
          padding: "10px 13px",
          borderRadius: "9px",
          cursor: "pointer",
          fontSize: "0.8125rem",
          background: mark === 0 ? "var(--c-surface-2)" : "transparent",
          border: mark === 0 ? "1px solid var(--c-brand)" : "1px solid rgba(17,20,28,.12)",
        }}
      >
        0 — does not reach a standard described by any descriptor
      </button>
    </div>
  );
}
